/**
 * Domain models for the multiple-propagation-constraint inspector.
 *
 * `TemporaryPath` and `EditedConstraint` are runtime classes (non-component
 * value exports), so they live in this sibling module rather than in the
 * component file — a non-component export in a `.tsx` file breaks Vite/
 * react-refresh Fast Refresh boundaries.
 */
import {
  pathIsHeadOf,
  pathIsTailOf,
  pathIsPartOf,
  pathStartsWith,
  pathEndsWith,
  overlappingTail,
} from '../../stores/model/metamodel/mpc_utils';
import type { IDependency } from '../../stores/model/metamodel/dependency';
import type { IInheritance } from '../../stores/model/metamodel/inheritance';
import type { IMultiplePropagationConstraint } from '../../stores/model/metamodel/multiple_propagation_constraint';

/**
 * A single edge in a propagation path. At runtime these are MST Dependency or
 * Inheritance references; the mimic classes below read the union-specific fields
 * (`name`/`dependent` on a dependency, `supertype` on an inheritance), so the
 * element shape declares them all as optional. `IPathElement` is what the
 * `Path` rendering and the structural `mpc_utils` helpers consume.
 */
export type IPathElement = IDependency | IInheritance;

/** A pending change set as stored by the MPC inspector (loose, by key). */
export type MPCChangeSet = {
  id: string;
  type?: string;
  constrainedObjectName?: string;
  primaryPath?: string;
  secondaryPath?: string;
};

/** A constraint property the `EditedConstraint` reads, keyed dynamically. */
export type ConstraintKey =
  | 'id'
  | 'type'
  | 'constrainedObjectName'
  | 'primaryPath'
  | 'secondaryPath';

/**
 * Class that mimics a Path in an MPC without actually creating a node in the
 * model for it.
 *
 * Only mimics the behavior needed for display purposes in this component.
 */
export class TemporaryPath {
  elements: IPathElement[];

  constructor(elements: IPathElement[]) {
    this.elements = elements;
  }

  get id() {
    if (this.elements != null) {
      return this.elements.map((e) => e.id).join('+');
    }
  }

  get firstObject() {
    if (this.elements != null && this.elements.length > 0) {
      return this.elements[0].previousObject;
    }
  }

  isHeadOf(elements: readonly IPathElement[]) {
    return pathIsHeadOf(this.elements, elements);
  }

  isTailOf(elements: readonly IPathElement[]) {
    return pathIsTailOf(this.elements, elements);
  }

  isPartOf(elements: readonly IPathElement[]) {
    return pathIsPartOf(this.elements, elements);
  }

  startsWith(elements: readonly IPathElement[]) {
    return pathStartsWith(this.elements, elements);
  }

  endsWith(elements: readonly IPathElement[]) {
    return pathEndsWith(this.elements, elements);
  }

  overlappingTail(elements: readonly IPathElement[]) {
    return overlappingTail(this.elements, elements);
  }

  overlappingDependencyTail(elements: readonly IPathElement[]) {
    // `overlappingTail` returns the structural `{ id }[]` shape the shared helper
    // declares, but the runtime members are the original path edges. View them
    // back as `IPathElement[]` for the inheritance/dependency discrimination.
    const commonTail = this.overlappingTail(elements) as unknown as IPathElement[];
    const reversedCommonTail = commonTail.reverse();
    const result: IPathElement[] = [];
    for (const element of reversedCommonTail) {
      // `supertype` only exists on the inheritance member of the union — its
      // presence marks an inheritance edge. Read it structurally.
      if ((element as IInheritance).supertype != null) {
        break;
      }
      result.push(element);
    }
    return result.reverse();
  }

  get accessorChain() {
    // We need to filter the dependencies only, check if there is dependent to check if an element
    // is a dependency.
    return this.elements.filter((e) => (e as IDependency).dependent != null).reverse();
  }
}

/** A map from constrained-object name to the available temporary paths. */
export type PathsByObjectType = Record<string, TemporaryPath[]>;

/** A path as the `Path` component / form consume it (real or mimic). */
export type DisplayPath = TemporaryPath | IPathType;

/** The `IPath` interface, re-exported under a local alias for the union above. */
type IPathType = IMultiplePropagationConstraint['primaryPath'];

/**
 * Class that represents an edited constraint
 */
export class EditedConstraint {
  original: IMultiplePropagationConstraint | undefined;
  changeSet: MPCChangeSet | undefined;
  paths: PathsByObjectType | null;

  constructor(
    original: IMultiplePropagationConstraint | undefined,
    changeSet: MPCChangeSet | undefined,
    paths: PathsByObjectType | null = null
  ) {
    this.original = original;
    this.changeSet = changeSet;
    this.paths = paths;
  }

  get valid() {
    return (
      this.type != null &&
      this.constrainedObjectName != null &&
      this.primaryPath != null &&
      this.secondaryPath != null
    );
  }

  get added() {
    return this.original == null;
  }

  property(key: ConstraintKey): string | undefined {
    if (this.changeSet != null && this.changeSet[key as keyof MPCChangeSet] != null) {
      return this.changeSet[key as keyof MPCChangeSet];
    } else if (this.original != null) {
      // `original` is the MST constraint; the read keys (`id`/`type`/
      // `constrainedObjectName`) are all string-valued on it.
      return (this.original as unknown as Record<ConstraintKey, string>)[key];
    }
  }

  get id(): string {
    // An edited constraint always carries an id (from its change set or its
    // original), so the dynamic lookup is non-null here.
    return this.property('id')!;
  }

  get type() {
    return this.property('type');
  }

  get constrainedObjectName() {
    return this.property('constrainedObjectName');
  }

  get primaryPath(): DisplayPath | undefined {
    if (this.added) {
      const relevantPaths = this.paths![this.constrainedObjectName!];
      return relevantPaths != null
        ? relevantPaths.find((p) => p.id === this.changeSet!.primaryPath)
        : undefined;
    } else {
      return this.original!.primaryPath;
    }
  }

  get secondaryPath(): DisplayPath | undefined {
    if (this.added) {
      const relevantPaths = this.paths![this.constrainedObjectName!];
      return relevantPaths != null
        ? relevantPaths.find((p) => p.id === this.changeSet!.secondaryPath)
        : undefined;
    } else {
      return this.original!.secondaryPath;
    }
  }
}
